import React, { useState } from "react";
import {
  Box,
  Link,
  TextField,
  Button,
  Typography,
  MenuItem,
  Select,
} from "@mui/material";
import PhoneIcon from "@mui/icons-material/Phone";
import { useNavigate } from "react-router-dom";

const carriers = [
  { value: "skymart", label: "Skymart Courier" },
  { value: "skymart-india", label: "Skymart India" }, // New carrier option
  { value: "dhl", label: "DHL" },
  { value: "ups", label: "UPS" },
  { value: "purolator", label: "Purolator" },
  { value: "ics", label: "ICS" },
  { value: "canpar", label: "Canpar" },
  { value: "neighbour-express", label: "Neighbour Express" },
];

const InfoBar = () => {
  const [trackingId, setTrackingId] = useState("");
  const [carrier, setCarrier] = useState("skymart"); // Default choice
  const navigate = useNavigate();

  const handleTrack = () => {
    if (!trackingId || !carrier) {
      alert("Please enter a tracking ID and select a carrier.");
      return;
    }

    if (carrier === "skymart-india") {
      // Internal navigation for Skymart India
      navigate(`./tracking?trackingId=${trackingId}`);
      return;
    }

    let url = "";

    if (carrier === "canpar") {
      url = `https://www.canpar.com/en/tracking/track.htm?barcode=${trackingId}`;
    } else if (carrier === "neighbour-express") {
      url = `https://www.neighbourexpress.com/track?trackingNumber=${trackingId}`;
    } else {
      switch (carrier) {
        case "dhl":
          url = `https://www.dhl.com/ca-en/home/tracking.html?tracking-id=${trackingId}`;
          break;
        case "ups":
          url = `https://www.ups.com/track?tracknum=${trackingId}`;
          break;
        case "purolator":
          url = `https://www.purolator.com/en/shipping/tracker?pins=${trackingId}`;
          break;
        case "ics":
          url = `https://www.icscourier.ca/online-services/parcel-tracking.aspx?trackNums=${trackingId}`;
          break;
        case "skymart":
          url = `https://skymart.ca/track-skymart-courier/?tracking_number=${trackingId}`;
          break;
        default:
          alert("Please select a valid carrier.");
          return;
      }
    }

    window.open(url, "_blank");
  };

  return (
    <Box
      sx={{
        position: "relative",
        background: "var(--gradient-blue)",
        color: "white",
        px: { xs: "1rem", md: "2rem" },
        py: "0.75rem",
        fontFamily: "'Inter', sans-serif",
        fontSize: { xs: "1rem", md: "0.9rem" },
        overflow: "hidden",
        boxShadow: "0 0.2rem 0.8rem rgba(0,0,0,0.15)",
        borderRadius: "0.25rem",
      }}
    >
      <TopGeometry />

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "center",
          justifyContent: "space-between",
          gap: "2rem",
          textAlign: { xs: "center", md: "left" },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "1rem",
            flexWrap: "wrap",
            justifyContent: { xs: "center", md: "flex-start" },
          }}
        >
          <Typography variant="subtitle1">Skymart Updates:</Typography>
          <Link
            href="https://skymartexpress.com"
            underline="always"
            color="inherit"
            variant="subtitle1"
            target="_blank"
            rel="noopener noreferrer"
          >
            Indian Economy Offer Available
          </Link>
          <span>|</span>
          <Link
            href="https://skymartexpress.com"
            underline="always"
            color="inherit"
            variant="subtitle1"
            target="_blank"
            rel="noopener noreferrer"
          >
            US Tariff Updates
          </Link>
        </Box>

        {/* Center phone (desktop only) */}
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            alignItems: "center",
            gap: "1rem",
            justifyContent: "center",
            flex: 1,
          }}
        >
          <PhoneIcon sx={{ color: "white", fontSize: "1.2rem" }} />
          <Typography variant="subtitle2" sx={{ fontSize: "0.9rem" }}>
            +1 800-808-0215
          </Typography>
        </Box>

        {/* Right side tracking form */}
        <Box
          sx={{
            width: { xs: "100%", md: "auto" },
            display: "flex",
            justifyContent: { xs: "center", md: "flex-end" },
            flex: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: "1rem",
              width: { xs: "100%", md: "auto" },
              alignItems: { xs: "stretch", md: "center" },
            }}
          >
            <Select
              size="small"
              value={carrier}
              onChange={(e) => setCarrier(e.target.value)}
              sx={{
                backgroundColor: "white",
                borderRadius: "0.5rem",
                minWidth: "10rem",
                height: "3rem",
                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                  py: 0,
                  height: "100%",
                },
                "& .MuiOutlinedInput-notchedOutline": { border: "none" },
              }}
            >
              {carriers.map((c) => (
                <MenuItem key={c.value} value={c.value}>
                  {c.label}
                </MenuItem>
              ))}
            </Select>

            <TextField
              size="small"
              placeholder="Enter Tracking ID"
              value={trackingId}
              onChange={(e) => setTrackingId(e.target.value)}
              fullWidth
              variant="outlined"
              sx={{
                backgroundColor: "white",
                borderRadius: "0.5rem",
                minWidth: { xs: "100%", md: "20rem" },
                height: "3rem",
                "& .MuiOutlinedInput-root": {
                  height: "100%",
                  "& input": {
                    py: 0,
                    px: "1.5rem",
                    fontSize: { xs: "1rem", md: "0.9rem" },
                  },
                  "& fieldset": { border: "none" },
                  "&:hover fieldset": { border: "none" },
                  "&.Mui-focused": { boxShadow: "var(--focus-glow)" },
                },
              }}
            />
            <Button
              variant="contained"
              onClick={handleTrack}
              sx={{
                backgroundColor: "#3b82f6",
                "&:hover": { backgroundColor: "#2563eb" },
                borderRadius: "0.5rem",
                px: { xs: "2rem", md: "2.4rem" },
                minWidth: { xs: "100%", md: "6rem" },
                height: "3rem",
              }}
            >
              Track
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const TopGeometry = () => (
  <>
    <svg
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        opacity: 0.08,
        zIndex: 0,
      }}
      width="9rem"
      height="9rem"
      viewBox="0 0 100 100"
    >
      <circle cx="50" cy="50" r="50" fill="var(--blob-blue)" />
    </svg>
    <svg
      style={{
        position: "absolute",
        top: 0,
        left: "25%",
        opacity: 0.06,
        zIndex: 0,
      }}
      width="10rem"
      height="10rem"
      viewBox="0 0 100 100"
    >
      <polygon points="0,0 100,0 80,100 0,80" fill="var(--blob-blue)" />
    </svg>
    <svg
      style={{
        position: "absolute",
        top: 0,
        left: "50%",
        opacity: 0.1,
        zIndex: 0,
      }}
      width="9rem"
      height="9rem"
      viewBox="0 0 100 100"
    >
      <circle cx="50" cy="50" r="50" fill="var(--blob-blue)" />
    </svg>
    <svg
      style={{
        position: "absolute",
        top: 0,
        left: "75%",
        opacity: 0.1,
        zIndex: 0,
      }}
      width="10rem"
      height="10rem"
      viewBox="0 0 100 100"
    >
      <polygon points="0,100 100,80 80,0 0,20" fill="var(--blob-blue)" />
    </svg>
    <svg
      style={{
        position: "absolute",
        top: 0,
        right: 0,
        opacity: 0.08,
        zIndex: 0,
      }}
      width="9rem"
      height="9rem"
      viewBox="0 0 100 100"
    >
      <circle cx="50" cy="50" r="50" fill="var(--blob-blue)" />
    </svg>
  </>
);

export default InfoBar;
